import { lazy } from 'react';

import { parseTwigJson } from '@app/utils/parseTwigJson';
import { ReactWebComponent } from '@app/utils/web-components';

import type { TranslationPick } from '.';
import { type TranslationDTO, convertMismatchDataDTOtoModel } from '../../../../../../types/translation';

const TranslationIcons = lazy(() => import('.'));
const AppProviders = lazy(() => import('@app/components/AppProviders'));

class TranslationIconsComponent extends ReactWebComponent {
    static get observedAttributes() {
        return [
            'translation',
            'can-ignore-qa',
            'is-html-mismatch-errors-visible',
            'is-inline-codes-mismatch-errors-visible',
            'is-placeholders-mismatch-errors-visible',
            'is-editing-translation',
            'is-bilingual-view',
            'is-segmentation-enabled',
            'score',
            'reference-lang-id',
        ];
    }

    render() {
        const isEditingTranslation = this.prop('is-editing-translation') === 'true';

        if (isEditingTranslation) {
            return null;
        }

        const translationProp = this.prop('translation');
        const translationDto = parseTwigJson<TranslationDTO>(translationProp);
        const score = this.jsonProp<number>('score');
        const referenceLangId = this.jsonProp<number>('reference-lang-id');

        const canIgnoreQATest = this.prop('can-ignore-qa') === 'true';
        const isHtmlMismatchErrorsVisible = this.prop('is-html-mismatch-errors-visible') === 'true';
        /*
         * TEMPORARY FIX: Using getAttribute() instead of this.prop() to handle missing attributes.
         *
         * Problem: this.prop() throws PropError when attribute is missing, but probably during rapid DOM
         * updates (AJAX-triggered re-renders), attributes may be temporarily missing due to race
         * conditions. getAttribute() returns null instead of throwing, which evaluates to false.
         *
         * The proper fix would be to ensure all attributes are preserved during dynamic updates
         * in the updatetranslationViews method in project.ts.
         * This will be investigated as a standalone task to implement a proper solution.
         */
        const isInlineCodesMismatchErrorsVisible =
            this.getAttribute('is-inline-codes-mismatch-errors-visible') === 'true';
        const isPlaceholdersMismatchErrorsVisible = this.prop('is-placeholders-mismatch-errors-visible') === 'true';
        const isBilingualView = this.prop('is-bilingual-view') === 'true';
        const isSegmentationEnabled = this.prop('is-segmentation-enabled') === 'true';

        const translation: TranslationPick = {
            translationId: parseInt(translationDto.id, 10),
            isPlural: translationDto.is_plural === '1',
            qaResults: translationDto.qa_results,
            mismatchResults: convertMismatchDataDTOtoModel(translationDto.p_mismatch_data),
            isBaseLanguage: translationDto.is_default === '1',
            langId: translationDto.lang_id,
            keyId: parseInt(translationDto.akey_id),
        };

        const handleQATestIgnored = (test: { testNumber: number }) => {
            this.dispatchEvent(new CustomEvent('onqatestignored', { detail: test.testNumber, bubbles: true }));
        };

        const handleMismatchErrorsIgnored = () => {
            this.dispatchEvent(new Event('onmismatcherrorsignored', { bubbles: true }));
        };

        const handleHtmlPlaceholdersVisibilityChanged = (visible: boolean) => {
            if (visible) {
                this.dispatchEvent(new Event('onmismatchpopovershow', { bubbles: true }));
            }
        };

        const handleScoreChanged = (score: number | null) => {
            this.dispatchEvent(new CustomEvent('onscorechanged', { detail: score, bubbles: true }));
        };

        return (
            <AppProviders>
                <TranslationIcons
                    score={score}
                    referenceLangId={referenceLangId}
                    translation={translation}
                    canIgnoreQATest={canIgnoreQATest}
                    onQATestIgnored={handleQATestIgnored}
                    onScoreChanged={handleScoreChanged}
                    isBilingualView={isBilingualView}
                    isSegmentationEnabled={isSegmentationEnabled}
                    isHtmlMismatchErrorsVisible={isHtmlMismatchErrorsVisible}
                    isInlineCodesMismatchErrorsVisible={isInlineCodesMismatchErrorsVisible}
                    isPlaceholdersMismatchErrorsVisible={isPlaceholdersMismatchErrorsVisible}
                    onMismatchErrorsIgnored={handleMismatchErrorsIgnored}
                    onHtmlPlaceholdersVisibilityChanged={handleHtmlPlaceholdersVisibilityChanged}
                />
            </AppProviders>
        );
    }
}

customElements.define('translation-icons', TranslationIconsComponent);
